import { useState, useCallback, useRef, useEffect } from "react";
import { RedeemVoucherRequest, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useTickets, usePollTicketUpdate } from "../useTickets";
import { useHubUser } from "../user/useHubUser";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UseVoucherCodeOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to handle voucher code redemption
 * - Submits voucher code to backend
 * - Implements 30-second cooldown after invalid submission
 * - Backend handles Mixpanel event tracking
 */
export const useVoucherCode = ({ onSuccess, onError }: UseVoucherCodeOptions = {}) => {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();

  // Don't fetch tickets upfront - we'll get them only when needed for polling
  const { tickets: currentTicketCount } = useTickets();

  // Get polling function
  const { pollForTicketUpdate } = usePollTicketUpdate();

  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldownRemaining(30);

    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }

    cooldownTimerRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const redeemMutation = useMutation({
    mutationFn: async (body: RedeemVoucherRequest): Promise<RedeemVoucherRequest> => {
      if (!hubUser?.id) {
        throw new Error("User not authenticated");
      }

      if (cooldownRemaining > 0) {
        throw new Error(`Please wait ${cooldownRemaining} seconds before trying again`);
      }

      // Submit the voucher code to the backend
      // Backend will handle reward granting asynchronously
      await apiHelper.postAuth<typeof API_ENDPOINT.HUB_VOUCHER_REDEEM>(auth, API_ENDPOINT.HUB_VOUCHER_REDEEM, {
        bodyParams: body,
      });

      // Poll for ticket count update to ensure UI reflects changes immediately
      await pollForTicketUpdate(currentTicketCount ?? 0);

      return body;
    },
    onSuccess: (voucherRequest) => {
      // Track successful redemption
      trackEvent(METRICS_EVENTS.VOUCHER_REDEEM_SUCCESS, {
        voucher_code: voucherRequest.code,
      });

      // Invalidate spinning wheel tickets
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SPINNING_WHEEL_TICKETS] });

      onSuccess?.();
    },
    onError: (error, voucherRequest) => {
      // Track failed redemption
      trackEvent(METRICS_EVENTS.VOUCHER_REDEEM_FAILED, {
        voucher_code: voucherRequest.code,
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      // Start cooldown on error (invalid code)
      startCooldown();
      onError?.(error as Error);
    },
  });

  return {
    redeemCode: redeemMutation.mutate,
    isRedeeming: redeemMutation.isPending,
    error: redeemMutation.error?.message ?? null,
    reset: redeemMutation.reset,
    cooldownRemaining,
    isOnCooldown: cooldownRemaining > 0,
  };
};
